import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import {
  useCarcaseAvailableYears,
  useCarcaseGauges,
} from "../hooks/useInsightsDashboardQueries";

// ─── Metric definitions ───
type MetricDef = {
  label: string;
  farmerKey: string;
  benchmarkKey: string;
  suffix: string;
  description: string;
  format: (v: number) => string;
  goodDirection: "lower" | "higher";
  idealRange?: string;
};

const METRICS: MetricDef[] = [
  {
    label: "Daily Liveweight Gain",
    farmerKey: "CarcaseSummary.estLifetimeDlwgFarmer",
    benchmarkKey: "CarcaseSummary.estLifetimeDlwgBenchmark",
    suffix: "kg/day",
    description:
      "The average daily weight gain across the animal's lifetime. Higher DLWG means more efficient growth, better feed conversion, and lower emissions per kg of beef produced.",
    format: (v) => v.toFixed(2),
    goodDirection: "higher",
    idealRange: "> 0.75 kg/day",
  },
  {
    label: "Average Slaughter Age",
    farmerKey: "CarcaseSummary.avgAgeAtSlaughterFarmer",
    benchmarkKey: "CarcaseSummary.avgAgeAtSlaughterBenchmark",
    suffix: "months",
    description:
      "The average age at which animals are finished. Lower slaughter age typically indicates efficient growth and reduces lifetime feed costs and methane emissions per animal.",
    format: (v) => v.toFixed(1),
    goodDirection: "lower",
    idealRange: "< 30 months",
  },
  {
    label: "Carcase Cold Weight",
    farmerKey: "CarcaseSummary.avgCarcaseColdWeightFarmer",
    benchmarkKey: "CarcaseSummary.avgCarcaseColdWeightBenchmark",
    suffix: "kg",
    description:
      "The average weight of the carcase after cooling. Optimal carcase weight varies by market but generally 270–420 kg indicates well-finished animals meeting processor specifications.",
    format: (v) => v.toFixed(0),
    goodDirection: "higher",
    idealRange: "270–420 kg",
  },
  {
    label: "Grade Specification",
    farmerKey: "CarcaseSummary.carcaseWithinDesiredGradeFarmer",
    benchmarkKey: "CarcaseSummary.carcaseWithinDesiredGradeBenchmark",
    suffix: "",
    description:
      "The proportion of carcases meeting the desired EUROP grade specification. Higher grade compliance means better market returns and indicates consistent finishing quality.",
    format: (v) => `${(v * 100).toFixed(0)}%`,
    goodDirection: "higher",
    idealRange: "> 90%",
  },
];

export function AnimalPerformanceTab({ farmerGuid }: { farmerGuid?: string }) {
  const { data: yearsData } = useCarcaseAvailableYears(farmerGuid);
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const row of yearsData?.data ?? []) {
      const y = row["CarcaseSummary.year"];
      if (y != null) years.add(Number(y));
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [yearsData?.data]);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (!selectedYear || !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const { data: gaugeData, isLoading } = useCarcaseGauges(
    farmerGuid,
    selectedYear
  );
  const row = gaugeData?.data?.[0];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!row) {
    return (
      <Card>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "#71717a" }}>
            No carcase data available
            {selectedYear ? ` for ${selectedYear}` : ""}. Data will appear here
            once slaughter records have been processed.
          </Typography>
          {availableYears.length > 0 && (
            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear ?? ""}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              sx={{ minWidth: 120 }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </Card>
    );
  }

  const periodStart = row["CarcaseSummary.periodStart"];
  const periodEnd = row["CarcaseSummary.periodEnd"];

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      {/* Overview */}
      <Card>
        {availableYears.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear ?? ""}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              sx={{ minWidth: 120 }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b", mb: 1 }}
        >
          Performance Overview
        </Typography>
        <Typography variant="body2" sx={{ color: "#52525b", lineHeight: 1.7 }}>
          Your herd's carcase performance compared to the programme benchmark.
          Each metric below shows your farm's average alongside the programme
          average, with context on what good looks like.
        </Typography>
        {periodStart && periodEnd && (
          <Typography
            variant="caption"
            sx={{ color: "#a1a1aa", display: "block", mt: 1 }}
          >
            Data period: {periodStart?.split("T")[0]} –{" "}
            {periodEnd?.split("T")[0]}
          </Typography>
        )}
      </Card>

      {/* Metric cards */}
      {METRICS.map((metric) => {
        const farmerVal = Number(row[metric.farmerKey as keyof typeof row]);
        const benchmarkVal = Number(
          row[metric.benchmarkKey as keyof typeof row]
        );
        const hasFarmer = !isNaN(farmerVal) && farmerVal > 0;
        const hasBenchmark = !isNaN(benchmarkVal) && benchmarkVal > 0;

        if (!hasFarmer) return null;

        const isBetter =
          metric.goodDirection === "higher"
            ? farmerVal >= benchmarkVal
            : farmerVal <= benchmarkVal;

        return (
          <MetricCard key={metric.label}>
            {/* Header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#18181b" }}
              >
                {metric.label}
              </Typography>
              {hasBenchmark && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {isBetter ? (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 16, color: "#2E7831" }}
                    />
                  ) : (
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#A48137" }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: isBetter ? "#2E7831" : "#A48137",
                    }}
                  >
                    {isBetter ? "Above" : "Below"} benchmark
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Values row */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 4 },
                mb: 2,
              }}
            >
              {/* Farmer value */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#71717a",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Your Farm
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    color: "#18181b",
                    lineHeight: 1.2,
                  }}
                >
                  {metric.format(farmerVal)}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#71717a",
                      ml: 0.5,
                    }}
                  >
                    {metric.suffix}
                  </Typography>
                </Typography>
              </Box>

              {/* Benchmark value */}
              {hasBenchmark && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#71717a",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Programme Avg
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.75rem",
                      fontWeight: 700,
                      color: "#a1a1aa",
                      lineHeight: 1.2,
                    }}
                  >
                    {metric.format(benchmarkVal)}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        color: "#a1a1aa",
                        ml: 0.5,
                      }}
                    >
                      {metric.suffix}
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Comparison bar */}
            {hasBenchmark && (
              <ComparisonBar
                farmerVal={farmerVal}
                benchmarkVal={benchmarkVal}
                isBetter={isBetter}
              />
            )}

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "#52525b",
                lineHeight: 1.6,
                fontSize: "0.825rem",
                mt: 2,
              }}
            >
              {metric.description}
            </Typography>

            {metric.idealRange && (
              <Typography
                variant="caption"
                sx={{
                  color: "#71717a",
                  backgroundColor: "#f4f4f5",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  display: "inline-block",
                  mt: 1,
                }}
              >
                Target: {metric.idealRange}
              </Typography>
            )}
          </MetricCard>
        );
      })}
    </Box>
  );
}

// ─── Comparison bar component ───
function ComparisonBar({
  farmerVal,
  benchmarkVal,
  isBetter,
}: {
  farmerVal: number;
  benchmarkVal: number;
  isBetter: boolean;
}) {
  const maxVal = Math.max(farmerVal, benchmarkVal) * 1.15;
  const farmerPct = (farmerVal / maxVal) * 100;
  const benchmarkPct = (benchmarkVal / maxVal) * 100;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Farmer bar */}
      <Box
        sx={{
          height: 20,
          backgroundColor: "#f4f4f5",
          borderRadius: 1,
          overflow: "hidden",
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            width: `${farmerPct}%`,
            height: "100%",
            backgroundColor: isBetter ? "#2E7831" : "#EFC52E",
            borderRadius: 1,
            transition: "width 0.5s ease",
          }}
        />
      </Box>
      {/* Benchmark bar */}
      <Box
        sx={{
          height: 20,
          backgroundColor: "#f4f4f5",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${benchmarkPct}%`,
            height: "100%",
            backgroundColor: "#d4d4d8",
            borderRadius: 1,
            transition: "width 0.5s ease",
          }}
        />
      </Box>
      {/* Labels */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#71717a", fontSize: "0.7rem" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: isBetter ? "#2E7831" : "#EFC52E",
              mr: 0.5,
              verticalAlign: "middle",
            }}
          />
          Your farm
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#71717a", fontSize: "0.7rem" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#d4d4d8",
              mr: 0.5,
              verticalAlign: "middle",
            }}
          />
          Programme avg
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Styled components ───
const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
});

const MetricCard = styled(Card)({});
